import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import {
  CancelTransferOrderResult,
  DiffReason,
  distributionService,
  SystemHealthReport,
  SystemMetricsReport,
  TransferDiffItem,
  TransferOrderListItem,
} from "../services/distributionService";

/**
 * B3.32-B3.37 — hooks para las 6 pantallas de analítica + métricas.
 *
 * refetchInterval fino para cada vista según ritmo operativo:
 *  - transferencias / denials / diffs: 60s (operativa en vivo).
 *  - métricas / health: 5 min (dashboard).
 */

export function useTransferOrdersList(params: {
  store?: string;
  source_type?: "seeding" | "stock_alert" | "reorder";
  status?: "draft" | "in_progress" | "received" | "cancelled";
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}): {
  data: { total: number; items: TransferOrderListItem[] } | undefined;
  isLoading: boolean;
  refetch: () => void;
} {
  const query = useQuery({
    queryKey: ["distribution", "transfer-orders", params],
    queryFn: () => distributionService.listTransferOrders(params),
    refetchInterval: 60_000,
  });
  return {
    data: query.data,
    isLoading: query.isLoading,
    refetch: () => {
      void query.refetch();
    },
  };
}

/**
 * Bug fix 2026-05-08 — hook para el endpoint manual de cancelacion de
 * transferencia. Tras éxito invalida el listado para que la fila refresque
 * su estado a `cancelled` sin esperar al `refetchInterval` de 60s.
 */
export function useCancelTransferOrder(): {
  mutateAsync: (input: {
    id: string;
    reason?: string;
  }) => Promise<CancelTransferOrderResult>;
  isPending: boolean;
} {
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => {
      const body: { reason?: string } = {};
      if (reason) body.reason = reason;
      return distributionService.cancelTransferOrder(id, body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ["distribution", "transfer-orders"],
      });
      void qc.invalidateQueries({
        queryKey: ["distribution", "pending-by-store"],
      });
    },
  });
  return {
    mutateAsync: (input) => mutation.mutateAsync(input),
    isPending: mutation.isPending,
  };
}

export function useDiffsList(params: {
  store?: string;
  product_id?: string;
  reason?: string;
  auto_resolved?: boolean;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}): {
  data: { total: number; items: TransferDiffItem[] } | undefined;
  isLoading: boolean;
  refetch: () => void;
} {
  const query = useQuery({
    queryKey: ["distribution", "diffs", params],
    queryFn: () => distributionService.listDiffs(params),
    refetchInterval: 60_000,
  });
  return {
    data: query.data,
    isLoading: query.isLoading,
    refetch: () => {
      void query.refetch();
    },
  };
}

export function useUpdateDiffReason(): {
  mutateAsync: (input: {
    id: number;
    reason: DiffReason;
    reason_detail?: string;
  }) => Promise<TransferDiffItem>;
  isPending: boolean;
} {
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: {
      id: number;
      reason: DiffReason;
      reason_detail?: string;
    }) => distributionService.updateDiffReason(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["distribution", "diffs"] });
    },
  });
  return {
    mutateAsync: (input) => mutation.mutateAsync(input),
    isPending: mutation.isPending,
  };
}

export function useStoreDenials(store: string | undefined): {
  data: ReturnType<
    typeof distributionService.getStoreDenials
  > extends Promise<infer T>
    ? T | undefined
    : never;
  isLoading: boolean;
} {
  const query = useQuery({
    queryKey: ["distribution", "denials", store],
    queryFn: () => {
      if (!store) return Promise.resolve([]);
      return distributionService.getStoreDenials(store);
    },
    enabled: !!store,
    refetchInterval: 60_000,
  });
  return {
    data: query.data as never,
    isLoading: query.isLoading,
  };
}

export function useSystemHealth(): {
  data: SystemHealthReport | undefined;
  isLoading: boolean;
  refetch: () => void;
} {
  const query = useQuery({
    queryKey: ["system", "health"],
    queryFn: () => distributionService.getSystemHealth(),
    refetchInterval: 5 * 60_000,
  });
  return {
    data: query.data,
    isLoading: query.isLoading,
    refetch: () => {
      void query.refetch();
    },
  };
}

export function useSystemMetrics(): {
  data: SystemMetricsReport | undefined;
  isLoading: boolean;
  refetch: () => void;
} {
  const query = useQuery({
    queryKey: ["system", "metrics"],
    queryFn: () => distributionService.getSystemMetrics(),
    refetchInterval: 5 * 60_000,
  });
  return {
    data: query.data,
    isLoading: query.isLoading,
    refetch: () => {
      void query.refetch();
    },
  };
}
